import { Box, Typography, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import usePickup from "hooks/pickups/usePickup";
import FileDropSection from "components/FileDropSection";
import { AddAPhoto } from "@mui/icons-material";
import useDirectUpload from "hooks/useDirectUpload";
import theme from "lib/theme";

interface PickupImagesProps {
  step: number;
  setImagesCompleted?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function PickupImages({ step, setImagesCompleted }: PickupImagesProps) {
  const { pickup, createImage, deleteImage } = usePickup();
  const { uploadAsync } = useDirectUpload({ keyPrefix: "/pickup/pickup_images/", service: "amazon_public" });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (pickup && pickup.pickupImages.length > 0) {
      setImagesCompleted?.(true);
    } else {
      setImagesCompleted?.(false);
    }
  }, [pickup, setImagesCompleted]);

  return (
    <Box>
      <Typography variant="h6" fontSize="18px" fontWeight="bold">
        {step}. Upload pictures, or attach a file of the items you will be repurposing (the more information the better)
      </Typography>
      <Typography mt={0.5} variant="body2">
        Pictures and item information help us prepare the crew with the right staff and trucks for seamless processing
      </Typography>

      <Box mt={3}>
        {uploading ? (
          <Box
            sx={{
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: "white",
              display: "flex",
              minHeight: "195px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <FileDropSection
            icon={<AddAPhoto color="primary" sx={{ fontSize: "32px" }} />}
            acceptHint={"jpg, jpeg, png, csv, xls, xlsx"}
            accept={{
              "image/png": [".png", ".jpeg", ".jpg"],
              "image/jpeg": [".png", ".jpeg", ".jpg"],
              "files/csv": [".csv", ".xls", ".xlsx"],
            }}
            documents={
              pickup && pickup.pickupImages.length > 0
                ? pickup.pickupImages.map((image) => ({
                    name: image.filename,
                    url: image.url,
                    onDelete() {
                      deleteImage(image.id);
                    },
                  }))
                : undefined
            }
            onUpload={async (files) => {
              setUploading(true);
              try {
                for (const file of files) {
                  const uploadedBlob = await uploadAsync(file);
                  await createImage(uploadedBlob.signed_id);
                }
              } finally {
                setUploading(false);
              }
            }}
            loading={uploading}
          />
        )}
      </Box>
    </Box>
  );
}
